import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useBlocker, useNavigate, useParams } from 'react-router-dom';
import * as Tabs from '@radix-ui/react-tabs';
import { AxiosError } from 'axios';
import { ArrowLeft, Check, Loader2, Pencil, Trash2, TriangleAlert } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { StatusBadge } from '@/components/ui/Badge';
import { PageHeader } from '@/components/layout/PageHeader';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/auth';
import { useSessionDetail } from '@/api/notes';
import { useCompleteSession, useDeleteSession } from '@/api/sessions';
import { useActiveSessionTypes } from '@/api/referenceData';
import { useUserDirectory } from '@/api/users';
import { NoteForm } from './NoteForm';
import { SessionEditForm, describeRecurrence } from './SessionEditForm';
import { SessionHistoryPanel } from './SessionHistoryPanel';
import { EvvControls } from './EvvControls';
import { DataCollectionPane } from '@/features/data-collection/DataCollectionPane';
import { useSettings } from '@/api/settings';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
/*
 * SessionPage — /sessions/:id.
 *
 * Landing destination from a calendar click. Owns read/update/delete and
 * the one-off → series promotion flow; SessionDialog handles create only.
 *
 * Three tabs:
 *   Detail — read-only summary toggled to <SessionEditForm> via Edit button
 *   Session Note — <NoteForm> when the session's type has a note_type;
 *                  otherwise a "no note type configured" alert
 *   Data Collection — <DataCollectionPane> rendering per-target entry widgets
 *
 * Tab content uses `forceMount` so in-progress edits (note drafts, data-
 * collection entries) survive tab switches — the child trees stay mounted
 * and Radix just toggles a hidden state we mirror with `data-[state=inactive]:hidden`.
 * That also means the navigation guard (see below) can ask both panes for
 * their saveAll handlers at any time, not just when they're visible.
 *
 * Unsaved-work navigation guard:
 *   - NoteForm + DataCollectionPane each report their dirty state via
 *     `onDirtyChange` and register a saveAll function through a caller-
 *     provided ref.
 *   - useBlocker intercepts in-app route changes when either side is dirty.
 *   - beforeunload adds the browser-level "discard changes?" prompt for
 *     tab close / reload / external navigation.
 *   - A dialog offers Abandon / Save & Leave / Cancel. "Save & Leave" runs
 *     both saveAll handlers in sequence; any error cancels the navigation
 *     and surfaces the error in the dialog.
 */

type TabKey = 'detail' | 'note' | 'data' | 'history';

function pad(n: number): string {
    return n.toString().padStart(2, '0');
}

function formatTime12(h: number, m: number): string {
    const ampm = h < 12 ? 'am' : 'pm';
    const hour12 = ((h + 11) % 12) + 1;
    return `${hour12}:${pad(m)}${ampm}`;
}

function formatDateLong(iso: string | null): string {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return '';
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
}

function formatDuration(minutes: number): string {
    if (minutes <= 0) return '';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const parts: string[] = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    return parts.join(' ');
}

export function SessionPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const canEdit = useAuthStore((s) => s.hasPermission('edit_scheduling'));
    const canCheckIn = useAuthStore((s) => s.hasPermission('view_scheduling'));
    const canDelete = useAuthStore((s) => s.hasPermission('delete_sessions'));
    const settings = useSettings();
    const evvEnabled = !!settings.data?.scheduling?.evvEnabled;

    const [tab, setTab] = useState<TabKey>('detail');
    const [isEditing, setIsEditing] = useState(false);
    const [completeError, setCompleteError] = useState<string | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const completeMut = useCompleteSession();
    const deleteMut = useDeleteSession();

    // ─── unsaved-work tracking ────────────────────────────────────────
    const [noteDirty, setNoteDirty] = useState(false);
    const [dataDirtyCount, setDataDirtyCount] = useState(0);
    const noteSaveRef = useRef<(() => Promise<void>) | null>(null);
    const dataSaveRef = useRef<(() => Promise<void>) | null>(null);
    const anyDirty = noteDirty || dataDirtyCount > 0;

    const { data: session, isLoading, isError } = useSessionDetail(id);
    const sessionTypes = useActiveSessionTypes();
    // The session-note tab already pulls the full staff directory; deriving
    // BCBAs from it avoids a second filtered request keyed against the same
    // table. Reads `staff.data` (cached) and filters client-side.
    const staffDirectory = useUserDirectory();
    const bcbas = useMemo(
        () => (staffDirectory.data ?? []).filter((u) => u.roleType === 'BCBA'),
        [staffDirectory.data],
    );

    const sessionTypeRow = useMemo(() => {
        if (!session?.serviceTypeId || !sessionTypes.data) return null;
        return sessionTypes.data.find((t) => t.id === session.serviceTypeId) ?? null;
    }, [session?.serviceTypeId, sessionTypes.data]);
    const sessionTypeName = sessionTypeRow?.name ?? '';
    // Administrative sessions (trainings, meetings) aren't tied to a client
    // or a physical location — hiding those fields keeps the detail view
    // focused on who/when for those rows.
    const isAdministrative = sessionTypeRow?.category === 'Administrative';

    // Route-change guard. Only blocks same-page → different-page transitions
    // so tab switches (which stay on /sessions/:id) don't trigger the dialog.
    const blocker = useBlocker(
        useCallback(
            ({ currentLocation, nextLocation }: { currentLocation: { pathname: string }; nextLocation: { pathname: string } }) =>
                anyDirty && currentLocation.pathname !== nextLocation.pathname,
            [anyDirty],
        ),
    );

    // Browser-level close / reload / external-URL prompt. The browser only
    // renders a generic dialog here — we can't show the three-way chooser
    // for this path, but we can at least stop an accidental close.
    useEffect(() => {
        if (!anyDirty) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [anyDirty]);

    if (isLoading) {
        return (
            <div className="p-6 max-w-[1100px] mx-auto h-full">
                <div className="flex items-center gap-2 text-sm text-text-muted">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading session…
                </div>
            </div>
        );
    }

    if (isError || !session) {
        return (
            <div className="p-6 max-w-[1100px] mx-auto h-full space-y-4">
                <Link to="/scheduling" className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-text">
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to scheduling
                </Link>
                <Alert variant="danger">Couldn't load this session. It may have been deleted or you don't have access.</Alert>
            </div>
        );
    }

    const hasNoteType = !!session.noteType;
    const noteStatusLabel = session.noteStatus || 'Not Started';
    const canEditNow = canEdit && session.status !== 'Finalized';
    // Status-only flip Scheduled → Complete. Available to anyone who can
    // view the session — staff working a session shouldn't need a scheduler
    // to close it out. Server enforces the same rule.
    const canMarkComplete = session.status === 'Scheduled';
    // Delete is a delete_sessions-permission action and is disallowed for
    // finalized sessions (server also enforces). The button surfaces on the
    // read-only detail view so a delete-only role doesn't have to enter
    // edit_scheduling-gated edit mode just to remove a session.
    const canDeleteNow = canDelete && session.noteStatus !== 'Finalized';
    const noteStarted = !!session.noteStatus && session.noteStatus !== 'Not Started';

    const handleMarkComplete = async () => {
        setCompleteError(null);
        try {
            await completeMut.mutateAsync(session.id);
        } catch (err) {
            const msg = err instanceof AxiosError
                ? err.response?.data?.error ?? 'Failed to mark session complete.'
                : 'Failed to mark session complete.';
            setCompleteError(msg);
        }
    };

    const handleDelete = async () => {
        setDeleteError(null);
        try {
            await deleteMut.mutateAsync(session.id);
            navigate('/scheduling');
        } catch (err) {
            const msg = err instanceof AxiosError
                ? err.response?.data?.error ?? 'Failed to delete session.'
                : 'Failed to delete session.';
            setDeleteError(msg);
        }
    };

    const startMin = session.startH * 60 + session.startM;
    const endMin = startMin + session.dur;
    const endH = Math.floor(endMin / 60);
    const endM = endMin % 60;

    const supervisingBcbaName = session.isSupervised && session.supervisingBcbaId
        ? bcbas.find((u) => u.id === session.supervisingBcbaId)
        : null;
    const supervisionLabel = session.isSupervised
        ? (supervisingBcbaName
            ? `Supervised by ${supervisingBcbaName.first} ${supervisingBcbaName.last}`
            : 'Supervised')
        : '';

    return (
        <div className="p-4 sm:p-6 max-w-[1100px] mx-auto h-full flex flex-col min-h-0">
            <div className="shrink-0">
                <PageHeader
                    back={{ onClick: () => navigate(-1) }}
                    title={session.label || sessionTypeName || 'Session'}
                    subtitle={session.client || undefined}
                    meta={
                        <>
                            <div className="flex items-center gap-2 text-sm">
                                <span className="hidden sm:inline text-2xs font-bold uppercase tracking-wider text-text-subtle">
                                    Session
                                </span>
                                <StatusBadge status={session.status || 'Scheduled'} />
                            </div>
                            {hasNoteType && (
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="hidden sm:inline text-2xs font-bold uppercase tracking-wider text-text-subtle">
                                        Note
                                    </span>
                                    <StatusBadge status={noteStatusLabel} />
                                </div>
                            )}
                        </>
                    }
                />
            </div>

            <Tabs.Root
                value={tab}
                onValueChange={(v) => {
                    // Leaving the Detail tab bails out of any in-flight
                    // edit so the user can't keep fiddling with a form
                    // that's no longer visible (and, for a finalized
                    // session, whose writes the server would reject).
                    if (v !== 'detail' && isEditing) setIsEditing(false);
                    setTab(v as TabKey);
                }}
                className="flex-1 min-h-0 flex flex-col mt-4"
            >
                <Tabs.List className="shrink-0 flex gap-1 border-b border-border">
                    {[
                        { id: 'detail' as const, label: 'Detail' },
                        { id: 'note' as const, label: 'Session Note' },
                        { id: 'data' as const, label: 'Data Collection' },
                        { id: 'history' as const, label: 'History' },
                    ].map((t) => (
                        <Tabs.Trigger
                            key={t.id}
                            value={t.id}
                            className={cn(
                                'px-3 py-2 text-sm font-medium border-b-2 -mb-px',
                                'data-[state=active]:text-text data-[state=active]:border-accent',
                                'data-[state=inactive]:text-text-muted data-[state=inactive]:border-transparent hover:text-text',
                            )}
                        >
                            {t.label}
                            {t.id === 'note' && noteDirty && (
                                <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-warning align-middle" aria-label="unsaved" />
                            )}
                            {t.id === 'data' && dataDirtyCount > 0 && (
                                <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-warning align-middle" aria-label="unsaved" />
                            )}
                        </Tabs.Trigger>
                    ))}
                </Tabs.List>

                <Tabs.Content
                    value="detail"
                    forceMount
                    className="flex-1 min-h-0 pt-5 data-[state=inactive]:hidden flex flex-col"
                >
                    {isEditing ? (
                        <Card className="flex-1 min-h-0 p-0 overflow-hidden flex flex-col">
                            <SessionEditForm
                                session={session}
                                onCancel={() => setIsEditing(false)}
                                onSaved={() => setIsEditing(false)}
                                onDeleted={() => navigate('/scheduling')}
                            />
                        </Card>
                    ) : (
                        <div className="flex-1 min-h-0 overflow-auto">
                            {(canMarkComplete || canEditNow || canDeleteNow) && (
                                <div className="flex items-center justify-end gap-2 mb-3">
                                    {canMarkComplete && (
                                        <Button
                                            variant="success"
                                            size="sm"
                                            onClick={handleMarkComplete}
                                            disabled={completeMut.isPending}
                                        >
                                            {completeMut.isPending
                                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                : <Check className="w-3.5 h-3.5" />}
                                            Mark complete
                                        </Button>
                                    )}
                                    {canEditNow && (
                                        <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
                                            <Pencil className="w-3.5 h-3.5" /> Edit session
                                        </Button>
                                    )}
                                    {canDeleteNow && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setConfirmDelete(true)}
                                            className="text-error-text hover:bg-error-soft"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" /> Delete
                                        </Button>
                                    )}
                                </div>
                            )}
                            {completeError && (
                                <Alert variant="danger" className="mb-3">{completeError}</Alert>
                            )}
                            {evvEnabled && canCheckIn && session.clientId && (
                                <EvvControls sessionId={session.id} sessionStatus={session.status || 'Scheduled'} />
                            )}
                            <Card>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                                    {!isAdministrative && <DetailItem label="Client" value={session.client} />}
                                    <DetailItem
                                        label="Staff"
                                        value={session.staff
                                            ? `${session.staff}${session.type ? ` · ${session.type.toUpperCase()}` : ''}`
                                            : ''}
                                    />
                                    <DetailItem label="Session type" value={sessionTypeName} />
                                    {!isAdministrative && <DetailItem label="Location" value={session.location} />}
                                    <DetailItem label="Date" value={formatDateLong(session.date)} />
                                    <DetailItem
                                        label="Time"
                                        value={
                                            session.date
                                                ? `${formatTime12(session.startH, session.startM)} – ${formatTime12(endH, endM)}  (${formatDuration(session.dur)})`
                                                : ''
                                        }
                                    />
                                    {session.seriesId && (
                                        <DetailItem
                                            label="Recurrence"
                                            value={describeRecurrence(session.series, session.date)}
                                            className="md:col-span-2"
                                        />
                                    )}
                                    {session.isSupervised && (
                                        <DetailItem
                                            label="Supervision"
                                            value={supervisionLabel}
                                            className="md:col-span-2"
                                        />
                                    )}
                                    <DetailItem
                                        label="Notes"
                                        value={session.notes}
                                        className="md:col-span-2"
                                    />
                                </div>
                            </Card>
                        </div>
                    )}
                </Tabs.Content>

                <Tabs.Content
                    value="note"
                    forceMount
                    className="flex-1 min-h-0 flex flex-col pt-5 data-[state=inactive]:hidden"
                >
                    {hasNoteType ? (
                        <Card className="flex-1 min-h-0 p-0 overflow-hidden flex flex-col">
                            <NoteForm
                                session={session}
                                noteType={session.noteType!}
                                onDirtyChange={setNoteDirty}
                                saveAllRef={noteSaveRef}
                            />
                        </Card>
                    ) : (
                        <Alert variant="info">
                            This session type doesn't have a note template configured. Add one from Settings → Session Types.
                        </Alert>
                    )}
                </Tabs.Content>

                <Tabs.Content
                    value="data"
                    forceMount
                    className="flex-1 min-h-0 overflow-auto pt-5 data-[state=inactive]:hidden"
                >
                    <DataCollectionPane
                        sessionId={session.id}
                        clientId={session.clientId}
                        readOnly={session.noteStatus === 'Finalized'}
                        onDirtyChange={setDataDirtyCount}
                        saveAllRef={dataSaveRef}
                    />
                </Tabs.Content>

                <Tabs.Content
                    value="history"
                    className="flex-1 min-h-0 overflow-auto pt-5 data-[state=inactive]:hidden"
                >
                    <SessionHistoryPanel sessionId={session.id} />
                </Tabs.Content>
            </Tabs.Root>

            {confirmDelete && (
                <Dialog open onOpenChange={(o) => { if (!o && !deleteMut.isPending) { setConfirmDelete(false); setDeleteError(null); } }}>
                    <DialogContent showClose={false}>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <TriangleAlert className="w-5 h-5 text-error" />
                                Delete this session?
                            </DialogTitle>
                            <DialogDescription>
                                {noteStarted
                                    ? 'A session note has been started for this session. Deleting will permanently remove the note draft and any data collection entries — this can\'t be undone.'
                                    : 'This will permanently remove the session and any associated data collection entries. This can\'t be undone.'}
                            </DialogDescription>
                        </DialogHeader>
                        {deleteError && <Alert variant="danger">{deleteError}</Alert>}
                        <DialogFooter>
                            <Button
                                variant="ghost"
                                onClick={() => { setConfirmDelete(false); setDeleteError(null); }}
                                disabled={deleteMut.isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleDelete}
                                disabled={deleteMut.isPending}
                            >
                                {deleteMut.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                Delete session
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            {blocker.state === 'blocked' && (
                <UnsavedChangesDialog
                    noteDirty={noteDirty}
                    dataDirtyCount={dataDirtyCount}
                    onCancel={() => blocker.reset()}
                    onAbandon={() => blocker.proceed()}
                    onSaveAndLeave={async () => {
                        // Run both saves in sequence. If either throws the
                        // dialog surfaces the error and navigation stays
                        // cancelled — the blocker keeps the transition
                        // queued until the user picks again.
                        if (noteDirty && noteSaveRef.current) await noteSaveRef.current();
                        if (dataDirtyCount > 0 && dataSaveRef.current) await dataSaveRef.current();
                        blocker.proceed();
                    }}
                />
            )}
        </div>
    );
}

function UnsavedChangesDialog({
    noteDirty, dataDirtyCount, onCancel, onAbandon, onSaveAndLeave,
}: {
    noteDirty: boolean;
    dataDirtyCount: number;
    onCancel: () => void;
    onAbandon: () => void;
    onSaveAndLeave: () => Promise<void>;
}) {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const summary = [
        noteDirty ? 'session note draft' : null,
        dataDirtyCount > 0 ? `${dataDirtyCount} data ${dataDirtyCount === 1 ? 'point' : 'points'}` : null,
    ].filter(Boolean).join(' and ');

    return (
        <Dialog open onOpenChange={(open) => { if (!open && !saving) onCancel(); }}>
            <DialogContent showClose={false}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <TriangleAlert className="w-5 h-5 text-warning" />
                        Unsaved changes
                    </DialogTitle>
                    <DialogDescription>
                        You have unsaved {summary}. Leaving now will lose your work unless you save first.
                    </DialogDescription>
                </DialogHeader>

                {error && <Alert variant="danger">{error}</Alert>}

                <DialogFooter>
                    <Button variant="ghost" onClick={onCancel} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={onAbandon}
                        disabled={saving}
                    >
                        Abandon &amp; Leave
                    </Button>
                    <Button
                        variant="primary"
                        onClick={async () => {
                            setError(null);
                            setSaving(true);
                            try {
                                await onSaveAndLeave();
                            } catch (err) {
                                const msg = extractErrorMessage(err, 'Save failed.');
                                setError(msg);
                            } finally {
                                setSaving(false);
                            }
                        }}
                        disabled={saving}
                    >
                        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                        Save &amp; Leave
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function DetailItem({
    label, value, className,
}: { label: string; value: React.ReactNode; className?: string }) {
    const blank =
        value === null
        || value === undefined
        || (typeof value === 'string' && value.trim() === '');
    return (
        <div className={cn('mb-3.5', className)}>
            <div className="text-2xs font-bold uppercase tracking-wider text-text-subtle mb-0.5">
                {label}
            </div>
            <div className="text-sm text-text whitespace-pre-wrap">
                {blank ? <span className="text-text-subtle">—</span> : value}
            </div>
        </div>
    );
}
